import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@app/store/authStore';
import api from '@shared/services/api';
import IncomingCallBanner from '@/features/shared/components/IncomingCallBanner';
import Avatar from '@shared/components/ui/Avatar';
import LanguageSwitcher from '@shared/components/ui/LanguageSwitcher';
import './DashboardLayout.css';

// Nav items reference i18n keys (under `sidebar.*`) instead of literal labels
// so the sidebar re-renders in the selected language when the user flips
// the topbar language switcher.
const NAV_ITEMS: Record<string, { labelKey: string; icon: string; path: string }[]> = {
  patient: [
    { labelKey: 'sidebar.dashboard',      icon: '\uD83C\uDFE0', path: '/patient/dashboard' },
    { labelKey: 'sidebar.appointments',   icon: '\uD83D\uDCC5', path: '/patient/appointments' },
    { labelKey: 'sidebar.book_visit',     icon: '\u2795',       path: '/patient/book' },
    { labelKey: 'sidebar.vitals',         icon: '\uD83D\uDCC8', path: '/patient/vitals' },
    { labelKey: 'sidebar.medications',    icon: '\uD83D\uDC8A', path: '/patient/medications' },
    { labelKey: 'sidebar.health_records', icon: '\uD83D\uDCCB', path: '/patient/records' },
    { labelKey: 'sidebar.documents',      icon: '\uD83D\uDCC4', path: '/patient/documents' },
    { labelKey: 'sidebar.lab_results',    icon: '\uD83E\uDDEA', path: '/patient/lab-results' },
    { labelKey: 'sidebar.prescriptions',  icon: '\uD83D\uDCDC', path: '/patient/prescriptions' },
    { labelKey: 'sidebar.pharmacy',       icon: '\uD83C\uDFEA', path: '/patient/pharmacy' },
    { labelKey: 'sidebar.shop',           icon: '\uD83D\uDED2', path: '/shop' },
    { labelKey: 'sidebar.billing',        icon: '\uD83D\uDCB3', path: '/patient/billing' },
    { labelKey: 'sidebar.refunds',        icon: '\uD83D\uDCB8', path: '/patient/refunds' },
    { labelKey: 'sidebar.lab_trends',     icon: '\uD83D\uDCC8', path: '/patient/lab-trends' },
    { labelKey: 'sidebar.dependants',     icon: '\uD83D\uDC6A', path: '/patient/dependants' },
    { labelKey: 'sidebar.favourites',     icon: '\u2B50',       path: '/patient/favourites' },
    { labelKey: 'sidebar.nurse_services', icon: '\uD83D\uDC69\u200D\u2695\uFE0F', path: '/patient/nurse-services' },
    { labelKey: 'sidebar.messages',       icon: '\uD83D\uDCAC', path: '/patient/messages' },
    { labelKey: 'sidebar.heka_ai',        icon: '\uD83E\uDD16', path: '/patient/ai' },
    { labelKey: 'sidebar.privacy',        icon: '\uD83D\uDD12', path: '/patient/privacy' },
  ],
  doctor: [
    { labelKey: 'sidebar.dashboard',     icon: '\uD83C\uDFE0', path: '/clinician/dashboard' },
    { labelKey: 'sidebar.patient_queue', icon: '\uD83D\uDC65', path: '/clinician/queue' },
    { labelKey: 'sidebar.schedule',      icon: '\uD83D\uDCC5', path: '/clinician/schedule' },
    { labelKey: 'sidebar.my_patients',   icon: '\uD83D\uDCCB', path: '/clinician/patients' },
    { labelKey: 'sidebar.consultations', icon: '\uD83D\uDCF9', path: '/clinician/consultations' },
    { labelKey: 'sidebar.soap_notes',    icon: '\uD83D\uDCDD', path: '/clinician/soap-notes' },
    { labelKey: 'sidebar.prescriptions', icon: '\uD83D\uDCDC', path: '/clinician/prescriptions' },
    { labelKey: 'sidebar.lab_orders',    icon: '\uD83E\uDDEA', path: '/clinician/lab-orders' },
    { labelKey: 'sidebar.messages',      icon: '\uD83D\uDCAC', path: '/clinician/messages' },
    { labelKey: 'sidebar.heka_ai',       icon: '\uD83E\uDD16', path: '/clinician/ai' },
    { labelKey: 'sidebar.analytics',     icon: '\uD83D\uDCCA', path: '/clinician/analytics' },
    { labelKey: 'sidebar.earnings',      icon: '\uD83D\uDCB0', path: '/clinician/earnings' },
    { labelKey: 'sidebar.credentials',   icon: '\uD83C\uDF93', path: '/clinician/credentials' },
  ],
  nurse: [
    { labelKey: 'sidebar.dashboard',           icon: '\uD83C\uDFE0', path: '/clinician/dashboard' },
    { labelKey: 'sidebar.patient_queue',       icon: '\uD83D\uDC65', path: '/clinician/queue' },
    { labelKey: 'sidebar.schedule',            icon: '\uD83D\uDCC5', path: '/clinician/schedule' },
    { labelKey: 'sidebar.home_visit_bookings', icon: '\uD83C\uDFE0', path: '/clinician/bookings' },
    { labelKey: 'sidebar.consultations',       icon: '\uD83D\uDCF9', path: '/clinician/consultations' },
    { labelKey: 'sidebar.soap_notes',          icon: '\uD83D\uDCDD', path: '/clinician/soap-notes' },
    { labelKey: 'sidebar.messages',            icon: '\uD83D\uDCAC', path: '/clinician/messages' },
    { labelKey: 'sidebar.heka_ai',             icon: '\uD83E\uDD16', path: '/clinician/ai' },
    { labelKey: 'sidebar.credentials',         icon: '\uD83C\uDF93', path: '/clinician/credentials' },
  ],
  physician_assistant: [
    { labelKey: 'sidebar.dashboard',     icon: '\uD83C\uDFE0', path: '/clinician/dashboard' },
    { labelKey: 'sidebar.patient_queue', icon: '\uD83D\uDC65', path: '/clinician/queue' },
    { labelKey: 'sidebar.schedule',      icon: '\uD83D\uDCC5', path: '/clinician/schedule' },
    { labelKey: 'sidebar.consultations', icon: '\uD83D\uDCF9', path: '/clinician/consultations' },
    { labelKey: 'sidebar.prescriptions', icon: '\uD83D\uDCDC', path: '/clinician/prescriptions' },
    { labelKey: 'sidebar.messages',      icon: '\uD83D\uDCAC', path: '/clinician/messages' },
    { labelKey: 'sidebar.heka_ai',       icon: '\uD83E\uDD16', path: '/clinician/ai' },
  ],
  pharmacist: [
    { labelKey: 'sidebar.dashboard',     icon: '\uD83C\uDFE0', path: '/pharmacy/dashboard' },
    { labelKey: 'sidebar.order_queue',   icon: '\uD83D\uDCE6', path: '/pharmacy/orders' },
    { labelKey: 'sidebar.inventory',     icon: '\uD83D\uDCCA', path: '/pharmacy/inventory' },
    { labelKey: 'sidebar.prescriptions', icon: '\uD83D\uDCDC', path: '/pharmacy/prescriptions' },
    { labelKey: 'sidebar.nhis_claims',   icon: '\uD83C\uDFDB', path: '/pharmacy/nhis' },
    { labelKey: 'sidebar.messages',      icon: '\uD83D\uDCAC', path: '/pharmacy/messages' },
    { labelKey: 'sidebar.analytics',     icon: '\uD83D\uDCCA', path: '/pharmacy/analytics' },
  ],
  lab_tech: [
    { labelKey: 'sidebar.dashboard',    icon: '\uD83C\uDFE0', path: '/laboratory/dashboard' },
    { labelKey: 'sidebar.test_orders',  icon: '\uD83E\uDDEA', path: '/laboratory/orders' },
    { labelKey: 'sidebar.result_entry', icon: '\uD83D\uDCDD', path: '/laboratory/results' },
    { labelKey: 'sidebar.test_catalog', icon: '\uD83D\uDCCB', path: '/laboratory/catalog' },
    { labelKey: 'sidebar.messages',     icon: '\uD83D\uDCAC', path: '/laboratory/messages' },
    { labelKey: 'sidebar.analytics',    icon: '\uD83D\uDCCA', path: '/laboratory/analytics' },
  ],
  // `admin` = facility administrator (per the role model). These link to the
  // facility workspace, which was previously orphaned (no nav pointed to it).
  // Platform-wide administration lives under `super_admin` below.
  admin: [
    { labelKey: 'sidebar.dashboard',         icon: '\uD83C\uDFE0', path: '/facility/dashboard' },
    { labelKey: 'sidebar.staff',             icon: '\uD83D\uDC65', path: '/facility/staff' },
    { labelKey: 'sidebar.shop_admin',        icon: '\uD83D\uDED2', path: '/admin/shop' },
    { labelKey: 'sidebar.analytics',         icon: '\uD83D\uDCCA', path: '/facility/analytics' },
    { labelKey: 'sidebar.facility_settings', icon: '\u2699\uFE0F',  path: '/facility/settings' },
  ],
  super_admin: [
    { labelKey: 'sidebar.dashboard',     icon: '\uD83C\uDFE0', path: '/admin/dashboard' },
    { labelKey: 'sidebar.users',         icon: '\uD83D\uDC65', path: '/admin/users' },
    { labelKey: 'sidebar.facilities',    icon: '\uD83C\uDFE5', path: '/admin/facilities' },
    { labelKey: 'sidebar.kyc_review',    icon: '\uD83C\uDF93', path: '/admin/kyc' },
    { labelKey: 'sidebar.nhis_claims',   icon: '\uD83C\uDFDB', path: '/admin/nhis' },
    { labelKey: 'sidebar.shop_admin',    icon: '\uD83D\uDED2', path: '/admin/shop' },
    { labelKey: 'sidebar.analytics',     icon: '\uD83D\uDCCA', path: '/admin/analytics' },
    { labelKey: 'sidebar.nhis_tariffs',  icon: '\uD83D\uDCB5', path: '/admin/nhis-tariffs' },
    { labelKey: 'sidebar.payouts',       icon: '\uD83C\uDFE6', path: '/admin/payouts' },
    { labelKey: 'sidebar.refunds',       icon: '\uD83D\uDCB8', path: '/admin/refunds' },
    { labelKey: 'sidebar.governance',    icon: '\uD83D\uDEE1\uFE0F', path: '/admin/governance' },
    { labelKey: 'sidebar.system_health', icon: '\u2699\uFE0F',  path: '/admin/health' },
    { labelKey: 'sidebar.integrations',  icon: '\uD83D\uDD0C', path: '/admin/integrations' },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef<HTMLDivElement | null>(null);

  // Poll the real unread-notification count instead of showing a permanent
  // hard-coded dot.
  useEffect(() => {
    let active = true;
    const load = () => {
      api.get('/notifications/unread-count')
        .then((r) => { if (active) setUnreadCount(r.data?.count ?? r.data?.unread_count ?? 0); })
        .catch(() => { /* non-blocking */ });
    };
    load();
    const id = setInterval(load, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [profileMenuOpen]);

  if (!user) return null;

  const navItems = NAV_ITEMS[user.role] || NAV_ITEMS.patient;
  const firstName = user.profile?.first_name?.trim() ?? '';
  const lastName  = user.profile?.last_name?.trim() ?? '';
  const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : user.email;
  const photoUrl = user.profile?.profile_photo ?? null;
  const settingsPath = '/settings';

  // Role-aware messages route (the old hard-coded clinician/patient guess was a
  // dead/forbidden link for pharmacist, lab_tech and admin roles).
  const messagesPath: string | null = (() => {
    switch (user.role) {
      case 'patient': return '/patient/messages';
      case 'doctor':
      case 'nurse':
      case 'physician_assistant': return '/clinician/messages';
      case 'pharmacist': return '/pharmacy/messages';
      case 'lab_tech': return '/laboratory/messages';
      default: return null; // admin / super_admin have no messages inbox
    }
  })();

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <div className="dashboard-layout">
      <IncomingCallBanner />
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <a href="/" className="sidebar-logo" aria-label="Heka.care home">
            {collapsed ? (
              <img
                src="/apple-touch-icon.png"
                alt="Heka.care"
                width="32"
                height="32"
                style={{ width: 32, height: 32, borderRadius: 6, display: 'block' }}
                decoding="async"
              />
            ) : (
              <img
                src="/logo.png"
                srcSet="/logo-small.png 240w, /logo-email.png 480w, /logo.png 1160w"
                sizes="200px"
                alt="Heka.care"
                width="200"
                height="47"
                style={{ height: 36, width: 'auto', display: 'block' }}
                decoding="async"
              />
            )}
          </a>
          <button className="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '\u276F' : '\u276E'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!collapsed && <span className="sidebar-label">{t(item.labelKey)}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <NavLink to="/settings" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
            <span className="sidebar-icon">{'\u2699\uFE0F'}</span>
            {!collapsed && <span className="sidebar-label">{t('sidebar.settings')}</span>}
          </NavLink>
          <button type="button" className="sidebar-link sidebar-logout" onClick={handleLogout}>
            <span className="sidebar-icon">{'\uD83D\uDEAA'}</span>
            {!collapsed && <span className="sidebar-label">{t('sidebar.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="dashboard-main">
        {/* Top nav */}
        <header className="topnav">
          <button className="topnav-menu-btn" onClick={() => setSidebarOpen(true)}>
            {'\u2630'}
          </button>

          <div className="topnav-search">
            <input type="text" placeholder={t('topnav.search_placeholder')} />
          </div>

          <div className="topnav-actions">
            <div className="topnav-lang">
              <LanguageSwitcher variant="dark" />
            </div>
            {messagesPath && (
              <NavLink to={messagesPath} className="topnav-icon-btn" title={t('topnav.messages')}>
                {'\uD83D\uDCAC'}
              </NavLink>
            )}
            <NavLink to="/notifications" className="topnav-icon-btn topnav-notif-btn" title={t('topnav.notifications')}>
              {'\uD83D\uDD14'}
              {unreadCount > 0 && <span className="topnav-notif-dot" aria-label={`${unreadCount} unread`} />}
            </NavLink>
            <div className="topnav-user" ref={profileRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="topnav-user-trigger"
                onClick={() => setProfileMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen ? 'true' : 'false'}
                aria-label={t('topnav.profile_menu_label', { name: displayName })}
              >
                <Avatar
                  size="sm"
                  name={displayName}
                  src={photoUrl}
                  seed={user.id}
                />
                <div className="topnav-user-info">
                  <div className="topnav-user-name">{displayName}</div>
                  <div className="topnav-user-role">{user.role.replace('_', ' ')}</div>
                </div>
                <span className="topnav-user-chev" aria-hidden="true">{'\u25BE'}</span>
              </button>

              {profileMenuOpen && (
                <div role="menu" className="topnav-user-menu">
                  <div className="topnav-user-menu-head">
                    <Avatar size="md" name={displayName} src={photoUrl} seed={user.id} />
                    <div style={{ minWidth: 0 }}>
                      <div className="topnav-user-menu-name">{displayName}</div>
                      <div className="topnav-user-menu-email">{user.email}</div>
                    </div>
                  </div>
                  <button type="button" role="menuitem" className="topnav-user-menu-item" onClick={() => { setProfileMenuOpen(false); navigate(settingsPath); }}>
                    <span style={{ marginRight: 10 }}>{'\u2699\uFE0F'}</span> {t('topnav.profile_settings')}
                  </button>
                  <button type="button" role="menuitem" className="topnav-user-menu-item" onClick={() => { setProfileMenuOpen(false); navigate('/notifications'); }}>
                    <span style={{ marginRight: 10 }}>{'\uD83D\uDD14'}</span> {t('topnav.notifications')}
                  </button>
                  <button type="button" role="menuitem" className="topnav-user-menu-item topnav-user-menu-danger" onClick={handleLogout}>
                    <span style={{ marginRight: 10 }}>{'\uD83D\uDEAA'}</span> {t('topnav.sign_out')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
